import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { store } from '../store.js';

export function useUIState({ startingPlayersConfirmed, currentLineup }) {
  // State
  const statsCollapsed = ref(true);
  const statsFullscreen = ref(false);
  const sidebarCollapsed = ref(localStorage.getItem('sidebar-collapsed') === 'true');
  const showMatchSummary = ref(false);
  const matchSummaryDismissed = ref(false);
  const showArchiveManager = ref(false);
  const showRosterSetup = ref(false);
  const matchReady = ref(false);
  const showHeaderMenu = ref(false);
  const showSetSummary = ref(false);
  const scoreActionCollapsed = ref(true);
  const pointOutcome = ref(null);
  let pointOutcomeTimer = null;

  // Computed
  const currentSetNumber = computed(() => store.match.currentSetIndex + 1);
  const matchFinished = computed(() => store.match.finished);
  const matchSetsWon = computed(() => (typeof store.match.setsWon === 'number' ? store.match.setsWon : 0));
  const matchSetsLost = computed(() => (typeof store.match.setsLost === 'number' ? store.match.setsLost : 0));

  const isMatchSummaryVisible = computed(() =>
    ((matchFinished.value && !matchSummaryDismissed.value) || showMatchSummary.value) && !showSetSummary.value
  );

  const currentSet = computed(() => store.match.sets[store.match.currentSetIndex]);
  const matchMetadata = computed(() => store.match.metadata);
  const currentPlayByPlay = computed(() => currentSet.value ? currentSet.value.points : []);

  const debugRolesDisplay = computed(() => {
    return currentLineup.value
      .map(item => {
        if (!item) return '';
        const role = item.roleCode || item.player?.role || '';
        return `${item.rotationPosition}:${role}`;
      })
      .filter(Boolean)
      .join(' | ');
  });

  const appClasses = computed(() => {
    const sidebarVisible = startingPlayersConfirmed.value && !showRosterSetup.value;
    return {
      'has-stats-footer': true,
      'stats-collapsed': statsCollapsed.value,
      'stats-fullscreen': statsFullscreen.value,
      'sidebar-open': sidebarVisible && !sidebarCollapsed.value,
      'sidebar-collapsed': sidebarVisible && sidebarCollapsed.value,
      'summary-open': isMatchSummaryVisible.value
    };
  });

  // Methods
  const onStatsToggle = () => {
    statsCollapsed.value = !statsCollapsed.value;
    if (statsCollapsed.value) {
      statsFullscreen.value = false;
    }
  };
  const onStatsFullscreenToggle = () => {
    statsFullscreen.value = !statsFullscreen.value;
    if (statsFullscreen.value && statsCollapsed.value) {
      statsCollapsed.value = false;
    }
  };
  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    localStorage.setItem('sidebar-collapsed', sidebarCollapsed.value);
  };

  const openMatchSummary = () => {
    matchSummaryDismissed.value = false;
    showMatchSummary.value = true;
  };

  const closeMatchSummary = () => {
    if (matchFinished.value) {
      matchSummaryDismissed.value = true;
    }
    showMatchSummary.value = false;
  };

  const toggleHeaderMenu = () => { showHeaderMenu.value = !showHeaderMenu.value; };

  const closeHeaderMenuOnClickOutside = () => {
    if (showHeaderMenu.value) {
      showHeaderMenu.value = false;
    }
  };

  const showPointOutcome = (outcome) => {
    if (pointOutcomeTimer) clearTimeout(pointOutcomeTimer);
    pointOutcome.value = outcome;
    pointOutcomeTimer = setTimeout(() => {
      pointOutcome.value = null;
    }, 2000);
  };

  const dismissPointOutcome = () => {
    if (pointOutcomeTimer) clearTimeout(pointOutcomeTimer);
    pointOutcome.value = null;
  };

  const openArchiveManager = () => { showArchiveManager.value = true; };
  const closeArchiveManager = () => { showArchiveManager.value = false; };

  // handleNewMatch, handleDiscardMatch, handleOpenArchives call workflow methods
  // that aren't extracted yet. They receive callbacks set after created().
  const _workflowCallbacks = { archiveAndResetMatch: null, clearLocalStorage: null };

  const setWorkflowCallbacks = (callbacks) => {
    _workflowCallbacks.archiveAndResetMatch = callbacks.archiveAndResetMatch;
    _workflowCallbacks.clearLocalStorage = callbacks.clearLocalStorage;
  };

  const handleNewMatch = () => {
    showHeaderMenu.value = false;
    _workflowCallbacks.archiveAndResetMatch?.();
  };

  const handleDiscardMatch = () => {
    showHeaderMenu.value = false;
    const ok = window.confirm('Start a new match without archiving? This will permanently discard the current match.');
    if (!ok) return;
    _workflowCallbacks.clearLocalStorage?.();
  };

  const handleOpenArchives = () => {
    showHeaderMenu.value = false;
    openArchiveManager();
  };

  // Lifecycle
  onMounted(() => {
    document.addEventListener('click', closeHeaderMenuOnClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', closeHeaderMenuOnClickOutside);
  });

  return {
    // State
    statsCollapsed, statsFullscreen, sidebarCollapsed, showMatchSummary, matchSummaryDismissed,
    showArchiveManager, showRosterSetup, matchReady, showHeaderMenu, showSetSummary, scoreActionCollapsed, pointOutcome,
    // Computed
    currentSetNumber, matchFinished, matchSetsWon, matchSetsLost, isMatchSummaryVisible, currentSet, matchMetadata,
    currentPlayByPlay, debugRolesDisplay, appClasses,
    // Methods
    onStatsToggle, onStatsFullscreenToggle, toggleSidebar, openMatchSummary, closeMatchSummary,
    toggleHeaderMenu, closeHeaderMenuOnClickOutside, openArchiveManager, closeArchiveManager,
    handleNewMatch, handleDiscardMatch, handleOpenArchives,
    setWorkflowCallbacks, showPointOutcome, dismissPointOutcome
  };
}
